import {
  isMediaCardString,
  keyToMediaCardType,
  type MediaCardString,
  type MediaCardType,
} from '@wandb/weave/common/types/media';
import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import React, {createContext, useCallback, useContext, useMemo} from 'react';

import {
  type SettingType,
  trackSetting,
} from '../../../services/analytics/workspaceSettingsEvents';
import {usePart, useViewAction} from '../../../state/views/hooks';
import * as PanelBankConfigActions from '../../../state/views/panelBankConfig/actions';
import * as WorkspaceSettingsActions from '../../../state/views/workspaceSettings/actions';
import {type DispatchableAction} from '../../../types/redux';
import {type ColorPaletteOption} from '../../../util/colors';
import {useDetectChanges} from '../../../util/react-help/detectReactPropChanges';
import {useKeyInfoQueryContext} from '../../MultiRunWorkspace/KeyInfoQueryContext';
import {type MediaBrowserPanelConfig} from '../../PanelMediaBrowser';
import {useSetWorkspaceSyncSliderSteps} from '../../PanelMediaBrowser/hooks/useSyncSliderSteps';
import {getMediaKeys} from '../../PanelMediaBrowser/utils/getMediaKeys';
import {useWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';
import {useWorkspaceLinePlotSettings} from './runLinePlots/utils/getLinePlotSettings';
import {
  SEMANTIC_LEGEND_PALETTE_DEFAULT,
  type SemanticLegendPalette,
} from './semanticLegend/utils/colorPalettes';
import {
  type Complete,
  type LinePlotSettings,
  type OrganizationPrefix,
  type RunColorOptions,
  type WorkspaceLayoutSettings,
} from './types';
import {getWorkspaceLayoutSettings} from './workspaceLayout/getWorkspaceLayoutSettings';
import {useUpdateLayoutSettingAndOrganizePrefix} from './workspaceLayout/useUpdateOrganizePrefix';
import {useUpdateShouldAutoGeneratePanels} from './workspaceLayout/useUpdateShouldAutoGeneratePanels';
import {useUpdateLayoutSettingAndSortPanels} from './workspaceLayout/useUpdateSortPanels';

export type WorkspaceSettingsContextType = {
  // settings
  workspaceLayout: Complete<WorkspaceLayoutSettings>;
  linePlot: Complete<LinePlotSettings>;
  media: {
    mediaCardTypes: MediaCardType[];
    mediaKeys: string[];
    workspaceMediaSettings: MediaBrowserPanelConfig;
    updateWorkspaceMediaSettings: (
      settings: Partial<MediaBrowserPanelConfig>
    ) => void;
    unsetWorkspaceMediaSetting: (key: keyof MediaBrowserPanelConfig) => void;
    /**
     * Sync the workspace's panels slider steps
     */
    setWorkspaceSliderSteps: (
      stepIndex: number,
      mediaSliderKey: string,
      sectionSliderSync: boolean
    ) => void;
  };
  colorPaletteOption: ColorPaletteOption | undefined;
  semanticLegendRunColorOptions: RunColorOptions | undefined;
  semanticLegendPalette: SemanticLegendPalette | undefined;
  // updaters
  updateSemanticLegendPalette: (
    palette: SemanticLegendPalette
  ) => DispatchableAction;
  updateWorkspaceLayoutSettings: (
    settings: Partial<WorkspaceLayoutSettings>
  ) => DispatchableAction;
  updateLinePlotWorkspaceSettings: (
    settings: Partial<LinePlotSettings>
  ) => DispatchableAction;
  updateAllLinePlotSectionSettings: (
    settings: Partial<LinePlotSettings>
  ) => DispatchableAction;
  updateShouldAutoGeneratePanels: (shouldEnable: boolean) => void;
  updateLayoutSettingAndSortPanels: (sortAlphabetically: boolean) => void;
  updateLayoutSettingAndOrganizePrefix: (
    autoOrganizePrefix: OrganizationPrefix
  ) => void;
  updateColorPaletteOption: (
    colorPaletteOption: ColorPaletteOption | undefined
  ) => void;

  // analytics
  trackWorkspaceSettingChange: (
    setting: SettingType,
    value: string | undefined
  ) => void;
};

export const WorkspaceSettingsContext = createContext<
  WorkspaceSettingsContextType | undefined
>(undefined);

type WorkspaceSettingsProps = {
  children: React.ReactNode;
};

/**
 * This context provider is used in conjuction with SectionSettingsContextProvider.
 * We use these two context providers to handle cascading settings logic.
 */
export const WorkspaceSettingsContextProvider = ({
  children,
}: WorkspaceSettingsProps) => {
  const {panelBankConfigRef, workspaceSettingsRef} = useWorkspaceRefsContext();
  // eslint-disable-next-line wandb/no-use-part-in-contexts -- TODO: migrate to reselect selectors
  const workspaceSettings = usePart(workspaceSettingsRef);

  const semanticLegendRunColorOptions =
    workspaceSettings?.semanticLegendRunColorOptions;

  // line plot related settings
  const linePlotSettings = useWorkspaceLinePlotSettings(workspaceSettings);
  const updateLinePlotWorkspaceSettings = useViewAction(
    workspaceSettingsRef,
    WorkspaceSettingsActions.updateLinePlotWorkspaceSettings
  );
  const updateAllLinePlotSectionSettings = useViewAction(
    panelBankConfigRef,
    PanelBankConfigActions.updateAllLinePlotSectionSettings
  );

  const updateColorPaletteOption = useViewAction(
    workspaceSettingsRef,
    WorkspaceSettingsActions.updateColorPaletteOption
  );

  const updateSemanticLegendPalette = useViewAction(
    workspaceSettingsRef,
    WorkspaceSettingsActions.updateSemanticLegendPalette
  );

  // workspace layout related settings
  const {showEmptySections, autoOrganizePrefix, sortAlphabetically} =
    getWorkspaceLayoutSettings(workspaceSettings);
  // Note: once feature is fully released on prod, move values up into `getWorkspaceLayoutSettings`
  const shouldAutoGeneratePanels =
    workspaceSettings?.shouldAutoGeneratePanels ?? true;

  // media related settings
  const {
    keyInfoQuery: {historyKeyInfo: keyInfo},
  } = useKeyInfoQueryContext();
  const mediaKeys = useMemo(() => {
    const {allMediaKeys} = getMediaKeys(keyInfo);
    return allMediaKeys;
  }, [keyInfo]);
  const mediaCardTypes = useMemo(() => {
    const {allMediaKeys, keyTypes} = getMediaKeys(keyInfo);
    return allMediaKeys
      ?.map(key => (key != null ? keyTypes[key] : null))
      ?.filter(
        (mediaString): mediaString is MediaCardString =>
          isNotNullOrUndefined(mediaString) && isMediaCardString(mediaString)
      )
      ?.map(keyToMediaCardType);
  }, [keyInfo]);
  const workspaceMediaSettings = workspaceSettings?.media;

  const updateWorkspaceLayoutSettings = useViewAction(
    workspaceSettingsRef,
    WorkspaceSettingsActions.updateWorkspaceLayoutSettings
  );
  const updateShouldAutoGeneratePanels =
    useUpdateShouldAutoGeneratePanels(workspaceSettingsRef);
  const updateLayoutSettingAndSortPanels =
    useUpdateLayoutSettingAndSortPanels();
  const updateLayoutSettingAndOrganizePrefix =
    useUpdateLayoutSettingAndOrganizePrefix();

  // update media settings
  const updateWorkspaceMediaSettings = useViewAction(
    workspaceSettingsRef,
    WorkspaceSettingsActions.updateWorkspaceMediaSettings
  );
  const unsetWorkspaceMediaSetting = useViewAction(
    workspaceSettingsRef,
    WorkspaceSettingsActions.unsetWorkspaceMediaSetting
  );

  const setWorkspaceSliderSteps = useSetWorkspaceSyncSliderSteps(
    workspaceMediaSettings,
    panelBankConfigRef
  );

  // analytics
  const trackWorkspaceSettingChange = useCallback(
    (setting: SettingType, value: string | undefined) => {
      trackSetting({
        action: 'change',
        page: 'workspace',
        settingLevel: 'workspace',
        setting,
        settingValue: value,
      });
    },
    []
  );

  const value = useMemo(
    () => ({
      // settings
      workspaceLayout: {
        autoOrganizePrefix,
        showEmptySections,
        shouldAutoGeneratePanels,
        sortAlphabetically,
      },
      linePlot: linePlotSettings,
      media: {
        mediaCardTypes,
        mediaKeys,
        workspaceMediaSettings: workspaceMediaSettings ?? {},
        updateWorkspaceMediaSettings,
        unsetWorkspaceMediaSetting,
        setWorkspaceSliderSteps,
      },
      colorPaletteOption: workspaceSettings?.colorPaletteOption,
      semanticLegendRunColorOptions,
      semanticLegendPalette: workspaceSettings?.semanticLegendPalette,

      // updaters
      updateWorkspaceLayoutSettings,
      updateLinePlotWorkspaceSettings,
      updateAllLinePlotSectionSettings,
      updateLayoutSettingAndSortPanels,
      updateShouldAutoGeneratePanels,
      updateLayoutSettingAndOrganizePrefix,
      updateColorPaletteOption,
      updateSemanticLegendPalette,
      // analytics
      trackWorkspaceSettingChange,
    }),
    [
      autoOrganizePrefix,
      showEmptySections,
      shouldAutoGeneratePanels,
      sortAlphabetically,
      linePlotSettings,
      mediaCardTypes,
      mediaKeys,
      workspaceMediaSettings,
      updateWorkspaceMediaSettings,
      unsetWorkspaceMediaSetting,
      setWorkspaceSliderSteps,
      workspaceSettings?.colorPaletteOption,
      workspaceSettings?.semanticLegendPalette,
      semanticLegendRunColorOptions,
      updateWorkspaceLayoutSettings,
      updateLinePlotWorkspaceSettings,
      updateAllLinePlotSectionSettings,
      updateLayoutSettingAndSortPanels,
      updateShouldAutoGeneratePanels,
      updateLayoutSettingAndOrganizePrefix,
      updateColorPaletteOption,
      updateSemanticLegendPalette,
      trackWorkspaceSettingChange,
    ]
  );

  useDetectChanges('WorkspaceSettingsContextProvider', {
    linePlotSettings,
    panelBankConfigRef,
    shouldAutoGeneratePanels,
    updateAllLinePlotSectionSettings,
    updateLinePlotWorkspaceSettings,
    updateShouldAutoGeneratePanels,
    workspaceSettingsRef,
  });

  return (
    <WorkspaceSettingsContext.Provider value={value}>
      {children}
    </WorkspaceSettingsContext.Provider>
  );
};

export const useWorkspaceSettingsContext = (): WorkspaceSettingsContextType => {
  const value = useContext(WorkspaceSettingsContext);

  if (value == null) {
    throw new Error(
      'useWorkspaceSettingsContext must be used within a WorkspaceSettingsContextProvider'
    );
  }

  return value;
};

export const useMaybeWorkspaceSettingsContext = ():
  | WorkspaceSettingsContextType
  | undefined => {
  return useContext(WorkspaceSettingsContext);
};

export function useWorkspaceSettingsSemanticLegendPalette(): SemanticLegendPalette {
  const workspaceSettings = useMaybeWorkspaceSettingsContext();
  return (
    workspaceSettings?.semanticLegendPalette ?? SEMANTIC_LEGEND_PALETTE_DEFAULT
  );
}
